// GOV-001 Phase 4 — Cost Rate Change Requests.
//
// Accounts Manager: create draft → submit (impact preview computed
// server-side) → status tracking. CFO: review impact + approve/reject.
// Rates NEVER change until CFO approves; the master keeps its old row
// (versioned) and the version ledger records every applied change.

import { useState } from 'react'
import {
  App, Badge, Button, Card, Descriptions, Drawer, Form, Input, InputNumber,
  Select, Space, Table, Tag, Typography, DatePicker, Alert, Popconfirm,
} from 'antd'
import { PlusOutlined, CheckOutlined, CloseOutlined, SendOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import apiClient from '@/shared/api/client'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text } = Typography

const BASE = '/costing/rate-change-requests'

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default', PENDING_CFO_APPROVAL: 'gold', APPROVED: 'blue',
  REJECTED: 'red', CANCELLED: 'default', APPLIED: 'green', SUPERSEDED: 'purple',
}

const RATE_TYPES = [
  'RAW_MATERIAL', 'COTTON_LANDED_COST', 'WIP_PROCESS_COST', 'LABOUR_RATE',
  'MAINTENANCE_RATE', 'UTILITY_RATE', 'PACKING_RATE', 'WASTE_RECOVERY_RATE',
  'OVERHEAD_RATE', 'OTHER',
]
const SCOPES = [
  { value: 'STANDARD_COST', label: 'Standard Cost (count-wise)' },
  { value: 'STAGE_CONVERSION', label: 'Stage Conversion Cost' },
  { value: 'COUNT_MASTER', label: 'Count Master rate' },
  { value: 'WASTE_CATEGORY', label: 'Waste Category rate' },
  { value: 'OTHER', label: 'Other (manual apply)' },
]

interface RateRequest {
  id: string
  cost_rate_type: string
  rate_scope: string
  target_id: string | null
  changes: Record<string, { old: string | null; new: string | null }>
  effective_from: string
  reason: string
  approval_status: string
  requested_at: string
  rejection_reason: string | null
  restatement_required: boolean
  impact_json: {
    modules?: { module: string; affected_count: number | null; confidence: string }[]
    restatement_required?: boolean
  }
  approval_timeline?: { decision: string; acted_at: string; remarks: string | null }[]
  versions?: Record<string, unknown>[]
}

export default function CostRateChangeRequestsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [createOpen, setCreateOpen] = useState(false)
  const [detail, setDetail] = useState<RateRequest | null>(null)

  const canCreate = usePermission('cost.rate.request.create')
  const canApprove = usePermission('cost.rate.request.approve')

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['cost-rate-requests'],
    queryFn: async () => (await apiClient.get<RateRequest[]>(BASE)).data,
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['cost-rate-requests'] })

  const createMut = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const newValues: Record<string, string> = {}
      const field = values.field as string
      newValues[field] = String(values.new_value)
      return (await apiClient.post(BASE, {
        cost_rate_type: values.cost_rate_type,
        rate_scope: values.rate_scope,
        target_id: values.target_id || null,
        new_values: newValues,
        effective_from: dayjs(values.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
        rate_unit: values.rate_unit || null,
        reason: values.reason,
        business_impact_note: values.business_impact_note || null,
      })).data
    },
    onSuccess: () => {
      message.success('Draft created'); setCreateOpen(false)
      form.resetFields(); refresh()
    },
    onError: (e: { response?: { data?: { detail?: string } } }) =>
      message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const action = (path: string, body?: unknown) =>
    useMutationFactory(path, body)

  function useMutationFactory(suffix: string, extra?: unknown) {
    return async (id: string, reason?: string) => {
      try {
        await apiClient.post(`${BASE}/${id}/${suffix}`,
          reason ? { reason } : extra ?? {})
        message.success(`${suffix} OK`)
        setDetail(null); refresh()
      } catch (e) {
        const err = e as { response?: { data?: { detail?: string } } }
        message.error(err?.response?.data?.detail ?? `${suffix} failed`)
      }
    }
  }
  const doSubmit = action('submit')
  const doApprove = action('approve')
  const doCancel = action('cancel')
  const doReject = async (id: string) => {
    const reason = window.prompt('Rejection reason (required):')
    if (!reason || reason.trim().length < 3) return
    await action('reject')(id, reason.trim())
  }

  const openDetail = async (id: string) => {
    const r = (await apiClient.get<RateRequest>(`${BASE}/${id}`)).data
    setDetail(r)
  }

  const columns = [
    { title: 'Type', dataIndex: 'cost_rate_type', render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag> },
    { title: 'Scope', dataIndex: 'rate_scope' },
    {
      title: 'Change', key: 'change',
      render: (_: unknown, r: RateRequest) =>
        Object.entries(r.changes || {}).map(([f, c]) => (
          <div key={f}><Text code>{f}</Text>: {c.old ?? '—'} → <Text strong>{c.new}</Text></div>
        )),
    },
    { title: 'Effective', dataIndex: 'effective_from' },
    {
      title: 'Status', dataIndex: 'approval_status',
      render: (v: string, r: RateRequest) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[v]}>{v.replace(/_/g, ' ')}</Tag>
          {r.restatement_required && <Tag color="orange">Restatement</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions', key: 'a',
      render: (_: unknown, r: RateRequest) => (
        <Space size="small">
          <Button size="small" onClick={() => openDetail(r.id)}>View</Button>
          {canCreate && r.approval_status === 'DRAFT' && (
            <Button size="small" type="primary" icon={<SendOutlined />}
              onClick={() => doSubmit(r.id)}>Submit</Button>
          )}
          {canApprove && r.approval_status === 'PENDING_CFO_APPROVAL' && (
            <>
              <Popconfirm title="Approve & apply this rate change?"
                onConfirm={() => doApprove(r.id)}>
                <Button size="small" type="primary" icon={<CheckOutlined />}>Approve</Button>
              </Popconfirm>
              <Button size="small" danger icon={<CloseOutlined />}
                onClick={() => doReject(r.id)}>Reject</Button>
            </>
          )}
          {canCreate && ['DRAFT', 'PENDING_CFO_APPROVAL'].includes(r.approval_status) && (
            <Button size="small" onClick={() => doCancel(r.id)}>Cancel</Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Cost Rate Change Requests"
        subtitle="Rate changes require CFO approval before they reach costing. Old rates are versioned, never overwritten."
        extra={canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New Request
          </Button>
        )}
      />
      <Table rowKey="id" size="small" loading={isLoading}
        dataSource={rows} columns={columns} pagination={{ pageSize: 20 }} />

      {/* Create drawer */}
      <Drawer title="New Cost Rate Change" open={createOpen} width={520}
        onClose={() => setCreateOpen(false)}
        extra={<Button type="primary" onClick={() => form.validateFields().then((v) => createMut.mutate(v))}
          loading={createMut.isPending}>Save Draft</Button>}>
        <Form form={form} layout="vertical">
          <Form.Item name="cost_rate_type" label="Cost Rate Type" rules={[{ required: true }]}>
            <Select options={RATE_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') }))} />
          </Form.Item>
          <Form.Item name="rate_scope" label="Rate Scope" rules={[{ required: true }]}>
            <Select options={SCOPES} />
          </Form.Item>
          <Form.Item name="target_id" label="Target Row ID"
            tooltip="UUID of the rate master row (from the relevant master page). Optional for scope OTHER.">
            <Input placeholder="e.g. UUID of the standard-cost row" />
          </Form.Item>
          <Form.Item name="field" label="Rate Field" rules={[{ required: true }]}
            tooltip="Column name on the target master, e.g. std_rm_cost_per_kg">
            <Input placeholder="std_rm_cost_per_kg" />
          </Form.Item>
          <Form.Item name="new_value" label="New Rate" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="rate_unit" label="Rate Unit">
            <Input placeholder="₹/kg" />
          </Form.Item>
          <Form.Item name="effective_from" label="Effective From" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, min: 3 }]}>
            <Input.TextArea rows={2} placeholder="Why is this rate changing?" />
          </Form.Item>
          <Form.Item name="business_impact_note" label="Business Impact Note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Detail drawer — impact preview + timeline (CFO review view) */}
      <Drawer title="Rate Change Detail" open={!!detail} width={560}
        onClose={() => setDetail(null)}>
        {detail && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {detail.restatement_required && (
              <Alert type="warning" showIcon message="Restatement required"
                description="This change back-dates into already-costed records. Historical FG costs will need restatement after apply." />
            )}
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[detail.approval_status]}>{detail.approval_status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Type / Scope">
                {detail.cost_rate_type} / {detail.rate_scope}
              </Descriptions.Item>
              <Descriptions.Item label="Changes">
                {Object.entries(detail.changes || {}).map(([f, c]) => (
                  <div key={f}><Text code>{f}</Text>: {c.old ?? '—'} → <Text strong>{c.new}</Text></div>
                ))}
              </Descriptions.Item>
              <Descriptions.Item label="Effective From">{detail.effective_from}</Descriptions.Item>
              <Descriptions.Item label="Reason">{detail.reason}</Descriptions.Item>
              {detail.rejection_reason && (
                <Descriptions.Item label="Rejection">{detail.rejection_reason}</Descriptions.Item>
              )}
            </Descriptions>

            <Card size="small" title="Downstream Impact Preview">
              {(detail.impact_json?.modules ?? []).map((m) => (
                <div key={m.module} style={{ marginBottom: 4 }}>
                  <Badge status={
                    m.confidence === 'EXACT' ? 'success'
                      : m.confidence === 'ESTIMATED' ? 'warning' : 'default'
                  } />
                  <Text strong> {m.module.replace(/_/g, ' ')}</Text>{' — '}
                  {m.affected_count === null
                    ? <Tag>DATA NOT AVAILABLE</Tag>
                    : <Text>{m.affected_count} record(s)</Text>}
                  {' '}<Tag color="default">{m.confidence}</Tag>
                </div>
              ))}
              {!(detail.impact_json?.modules ?? []).length && (
                <Text type="secondary">Computed on submit.</Text>
              )}
            </Card>

            {(detail.approval_timeline ?? []).length > 0 && (
              <Card size="small" title="Audit Timeline">
                {detail.approval_timeline!.map((a, i) => (
                  <div key={i}>
                    <Tag>{a.decision}</Tag> {dayjs(a.acted_at).format('DD MMM HH:mm')}
                    {a.remarks && <Text type="secondary"> — {a.remarks}</Text>}
                  </div>
                ))}
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </>
  )
}
